


// src/components/sections/ContactFormSection.js
import axios from 'axios';
import { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const ContactFormSection = ({ content, sectionId  }) => {
  const [formData, setFormData] = useState({});
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    error: false,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  
  // Initialize form fields based on configuration or defaults
  useEffect(() => {
    const initialFormData = {};
    
    // Get form fields from content or use defaults
    const formFields = content?.formFields || [
      { id: 'name', type: 'text', required: true },
      { id: 'email', type: 'email', required: true },
      { id: 'phone', type: 'tel', required: false },
      { id: 'company', type: 'text', required: false },
      { id: 'message', type: 'textarea', required: true }
    ];
    
    // Initialize form data with empty values
    formFields.forEach(field => {
      initialFormData[field.id] = '';
    });
    
    setFormData(initialFormData);
  }, [content?.formFields]);
  
  // Extract content props or use defaults
  const title = content?.title || "Send Us a Message";
  const submitButtonText = content?.submitButtonText || "Send Message";
  const successMessage = content?.successMessage || "Thank you for your message! We'll get back to you soon.";
  const errorMessage = content?.errorMessage || "Sorry, there was an error sending your message. Please try again.";
  const contactInfo = content?.contactInfo || {
    phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
    email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
    address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
    hours: {
      weekday: "Monday - Friday: 9 AM - 5 PM",
      weekend: "Saturday - Sunday: Closed"
    },
    socialMedia: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: ""
    }
  };
  
  // Layout and style properties
  const showContactInfo = content?.showContactInfo !== false;
  const layoutType = content?.layoutType || 'side-by-side';
  const backgroundColor = content?.backgroundColor || "#1A4B84";
  const textColor = content?.textColor || "#ffffff";
  const formBackgroundColor = content?.formBackgroundColor || "#ffffff";
  const buttonColor = content?.buttonColor || backgroundColor;
  const buttonTextColor = content?.buttonTextColor || "#ffffff";
  const fieldBorderColor = content?.fieldBorderColor || "#e5e7eb";
  const borderRadius = content?.borderRadius || "rounded-xl";
  const shadowStyle = content?.shadowStyle || "shadow-xl";
  const padding = content?.padding || "p-12";
  const fieldBorderRadius = content?.fieldBorderRadius || "rounded-lg";
  const showSocialIcons = content?.showSocialIcons !== false;
  const buttonFullWidth = content?.buttonFullWidth !== false;
  const enableCaptcha = content?.enableCaptcha === true;
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // CAPTCHA change handler
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };
  
  // Form validation
  const validateForm = () => {
    // Get form fields or use defaults
    const formFields = content?.formFields || [
      { id: 'name', type: 'text', required: true },
      { id: 'email', type: 'email', required: true },
      { id: 'message', type: 'textarea', required: true }
    ];
    
    // Check each required field
    for (const field of formFields) {
      if (field.required && !formData[field.id]) {
        setSubmitStatus({ 
          success: false, 
          error: true, 
          message: `Please fill in the ${field.label || field.id} field.` 
        });
        return false;
      }
    }
    
    // Check email format if there's an email field
    const emailField = formFields.find(field => field.type === 'email');
    if (emailField && formData[emailField.id]) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData[emailField.id])) {
        setSubmitStatus({ 
          success: false, 
          error: true, 
          message: 'Please enter a valid email address.' 
        });
        return false;
      }
    }
    
    // Check CAPTCHA if enabled
    if (enableCaptcha && !captchaValue) {
      setSubmitStatus({ 
        success: false, 
        error: true, 
        message: 'Please complete the CAPTCHA verification.' 
      });
      return false;
    }

    return true;
  };
  
  // Form submission handler
// In ContactFormSection.js - Update the handleSubmit function

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Reset previous status
  setSubmitStatus({ success: false, error: false, message: '' });
  
  // Validate form
  if (!validateForm()) return;

  // Prevent multiple submissions
  if (isSubmitting) return;
  setIsSubmitting(true);

  try {
    // Create field mapping for better email formatting
    const fieldMapping = {};
    const formFields = content?.formFields || [
      { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
      { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
      { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
      { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
      { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
    ];

    // Create mapping of field IDs to labels
    formFields.forEach(field => {
      fieldMapping[field.id] = field.label || field.id;
    });

    // Create data object with form values, field mapping, sectionId and CAPTCHA token if enabled
    const submitData = {
      formData: formData,
      fieldMapping: fieldMapping,
      sectionId: sectionId, // This should be passed from the parent component
      enableSmsNotifications: content?.enableSmsNotifications, // Pass SMS settings
      adminPhoneNumbers: content?.adminPhoneNumbers,
      smsTemplate: content?.smsTemplate,
      ...(enableCaptcha && { captchaToken: captchaValue })
    };
    
    console.log('Submitting form data with sectionId:', sectionId);
    
    // Send form data to backend
    const response = await axios.post('/api/contact', submitData);
    
    setSubmitStatus({
      success: true,
      error: false,
      message: successMessage
    });
    
    // Reset form
    const resetFormData = {};
    Object.keys(formData).forEach(key => {
      resetFormData[key] = '';
    });
    setFormData(resetFormData);
    
    // Reset CAPTCHA
    if (enableCaptcha) {
      setCaptchaValue(null);
      if (window.grecaptcha) {
        try {
          window.grecaptcha.reset();
        } catch (err) {
          console.error('Error resetting CAPTCHA:', err);
        }
      }
    }
  } catch (error) {
    setSubmitStatus({
      success: false,
      error: true,
      message: error.response?.data?.message || errorMessage
    });
    console.error('Form submission error:', error);
  } finally {
    setIsSubmitting(false);
  }
};

// // working one
// const handleSubmit = async (e) => {
//   e.preventDefault();
  
//   // Reset previous status
//   setSubmitStatus({ success: false, error: false, message: '' });
  
//   // Validate form
//   if (!validateForm()) return;

//   // Prevent multiple submissions
//   if (isSubmitting) return;
//   setIsSubmitting(true);

//   try {
//     // Create field mapping for better email formatting
//     const fieldMapping = {};
//     const formFields = content?.formFields || [
//       { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
//       { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
//       { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
//       { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
//       { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
//     ];

//     // Create mapping of field IDs to labels
//     formFields.forEach(field => {
//       fieldMapping[field.id] = field.label || field.id;
//     });

//     // Create data object with form values, field mapping, and CAPTCHA token if enabled
//     const submitData = {
//       formData: formData,
//       fieldMapping: fieldMapping,
//       sectionId: sectionId,
//       ...(enableCaptcha && { captchaToken: captchaValue })
//     };
    
//     // Send form data to backend
//     const response = await axios.post('/api/contact', submitData);
    
//     setSubmitStatus({
//       success: true,
//       error: false,
//       message: successMessage
//     });
    
//     // Reset form
//     const resetFormData = {};
//     Object.keys(formData).forEach(key => {
//       resetFormData[key] = '';
//     });
//     setFormData(resetFormData);
    
//     // Reset CAPTCHA
//     if (enableCaptcha) {
//       setCaptchaValue(null);
//       // Attempt to reset CAPTCHA widget if the ref is available
//       if (window.grecaptcha) {
//         try {
//           window.grecaptcha.reset();
//         } catch (err) {
//           console.error('Error resetting CAPTCHA:', err);
//         }
//       }
//     }
//   } catch (error) {
//     setSubmitStatus({
//       success: false,
//       error: true,
//       message: error.response?.data?.message || errorMessage
//     });
//     console.error('Form submission error:', error);
//   } finally {
//     setIsSubmitting(false);
//   }
// };

  // Render a form field based on its type
const renderFormField = (field, index) => {
  console.log(`Rendering field ${index}:`, field);
  console.log('Field type:', field.type);
  console.log('Field width:', field.width);

  // Determine the width class based on field configuration
  let widthClass = 'col-span-2'; // Default to full width
  if (field.width === 'half') {
    widthClass = 'sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1';
  } else if (field.width === 'third') {
    widthClass = 'md:col-span-1 lg:col-span-1';
  }

  const fieldClasses = `${field.width} px-3 py-2 border ${fieldBorderRadius} focus:outline-none focus:ring-2 focus:ring-blue-500 w-full`;
  const fieldStyle = { borderColor: fieldBorderColor };
  console.log('Computed width class:', widthClass);

  switch (field.type) {
    case 'text':
    case 'email':
    case 'tel':
    case 'number':
    case 'url':
    case 'password':
      console.log(`Rendering ${field.type} input for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <input
            type={field.type}
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            placeholder={field.placeholder || ''}
            className={fieldClasses}
            style={fieldStyle}
            required={field.required}
            min={field.type === 'number' ? field.min : undefined}
            max={field.type === 'number' ? field.max : undefined}
            step={field.type === 'number' ? field.step : undefined}
          />
        </div>
      );

    case 'time':
      console.log(`Rendering time input for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <input
            type="time"
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className={fieldClasses}
            style={fieldStyle}
            required={field.required}
          />
        </div>
      );

    case 'date':
      console.log(`Rendering date input for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <input
            type="date"
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className={fieldClasses}
            style={fieldStyle}
            required={field.required}
            min={field.min}
            max={field.max}
          />
        </div>
      );

    case 'textarea':
      console.log(`Rendering textarea for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <textarea
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            placeholder={field.placeholder || ''}
            rows={field.rows || 5}
            className={fieldClasses}
            style={fieldStyle}
            required={field.required}
          />
        </div>
      );

    case 'select':
      console.log(`Rendering select for ${field.id}`);
      // Parse options from comma-separated string or use array
      const selectOptions = typeof field.options === 'string' 
        ? field.options.split(',').map(opt => opt.trim())
        : field.options || [];

      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <select
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className={fieldClasses}
            style={fieldStyle}
            required={field.required}
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {selectOptions.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );

    case 'radio':
      console.log(`Rendering radio group for ${field.id}`);
      // Parse options from comma-separated string or use array
      const radioOptions = typeof field.options === 'string' 
        ? field.options.split(',').map(opt => opt.trim())
        : field.options || [];

      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <div className={`space-${field.radioInline ? 'x' : 'y'}-2 ${field.radioInline ? 'flex flex-wrap' : ''}`}>
            {radioOptions.map((option, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.id}_${idx}`}
                  name={field.id}
                  value={option}
                  checked={formData[field.id] === option}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  style={{ borderColor: fieldBorderColor }}
                  required={field.required}
                />
                <span className="ml-2 text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        </div>
      );

    case 'checkbox':
      console.log(`Rendering checkbox for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          <label className="flex items-center">
            <input
              type="checkbox"
              id={field.id}
              name={field.id}
              checked={!!formData[field.id]}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              style={{ borderColor: fieldBorderColor }}
              required={field.required}
            />
            <span className="ml-2 text-sm text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </span>
          </label>
        </div>
      );

    case 'file':
      console.log(`Rendering file input for ${field.id}`);
      return (
        <div key={field.id} className={widthClass}>
          {field.label && (
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
          )}
          <input
            type="file"
            id={field.id}
            name={field.id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            style={{ borderColor: fieldBorderColor }}
            required={field.required}
            accept={field.accept}
            multiple={field.multiple}
          />
          {field.helpText && (
            <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
          )}
        </div>
      );

    default:
      console.log(`Unknown field type: ${field.type}`);
      return null;
  }
};

  
  // Social Media Icons
  const renderSocialIcon = (platform, url) => {
    if (!url) return null;
    
    let icon;
    switch (platform) {
      case 'facebook':
        icon = (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
          </svg>
        );
        break;
      case 'twitter':
        icon = (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.035 10.035 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z" />
          </svg>
        );
        break;
      case 'instagram':
        icon = (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
        );
        break;
      case 'linkedin':
        icon = (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        );
        break;
      default:
        return null;
    }
    
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-white hover:text-gray-200 transition-colors"
        key={platform}
      >
        {icon}
      </a>
    );
  };
  
  // Apply custom CSS if provided
  useEffect(() => {
    if (content?.customCss) {
      // Create a style element
      const styleElement = document.createElement('style');
      styleElement.textContent = content.customCss;
      document.head.appendChild(styleElement);
      
      // Clean up when component unmounts
      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [content?.customCss]);
  
  // Determine form layout classes based on configuration
  const getLayoutClasses = () => {
    switch (layoutType) {
      case 'form-top':
        return {
          container: `grid grid-cols-1 gap-8`,
          formSection: 'order-1',
          infoSection: 'order-2'
        };
      case 'info-top':
        return {
          container: `grid grid-cols-1 gap-8`,
          formSection: 'order-2',
          infoSection: 'order-1'
        };
      case 'form-only':
        return {
          container: ``,
          formSection: 'w-full',
          infoSection: 'hidden'
        };
      case 'side-by-side':
      default:
        return {
          container: `grid md:grid-cols-2 gap-8`,
          formSection: '',
          infoSection: ''
        };
    }
  };
  
  const layoutClasses = getLayoutClasses();
  
  // Get form fields from content or use defaults
  const formFields = content?.formFields || [
    { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
    { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
    { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
    { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
    { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
  ];

  return (
    <div className={`${borderRadius} overflow-hidden ${shadowStyle} bg-white contact-form-section`}>
      <div className={layoutClasses.container}>
        {/* Contact Information Section */}
        {showContactInfo && (
          <div 
            className={`${padding} flex flex-col justify-center ${layoutClasses.infoSection}`} 
            style={{ backgroundColor, color: textColor }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {content?.contactPanelTitle || `Contact ${process.env.REACT_APP_COMPANY_NAME || 'Us'}`}
            </h2>

            <div className="space-y-6">
              {(content?.infoSectionText || content?.infoSectionDescription) && (
                <div className="mb-6 space-y-3">
                  {content?.infoSectionText && (
                    <div className="text-lg leading-relaxed">
                      {content.infoSectionText}
                    </div>
                  )}
                  {content?.infoSectionDescription && (
                    <div className="text-base opacity-90 leading-relaxed">
                      {content.infoSectionDescription}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center space-x-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-lg">{contactInfo.phone}</span>
              </div>
              <div className="flex items-center space-x-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-lg">{contactInfo.email}</span>
              </div>
              {contactInfo.address && (
                <div className="flex items-center space-x-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-lg">{contactInfo.address}</span>
                </div>
              )}
              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Business Hours</h3>
                <p className="text-lg">{contactInfo.hours.weekday}</p>
                <p className="text-lg">{contactInfo.hours.weekend}</p>
              </div>
              
              {/* Social Media Icons */}
              
              {showSocialIcons && (
                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-3">Connect With Us</h3>
                  <div className="flex space-x-4">
                    {Object.entries(contactInfo.socialMedia || {}).map(([platform, url]) => 
                      url && renderSocialIcon(platform, url)
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Form Section */}
        <div 
          className={`${padding} ${layoutClasses.formSection}`}
          style={{ backgroundColor: formBackgroundColor }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{title}</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
           <div className="grid grid-cols-2 gap-4">

            {/* <div className="grid grid-cols-2 gap-4">
              {formFields.map((field, index) => renderFormField(field, index))}
            </div>
             */}
            {/* Fallback to direct form fields if formFields mapping fails */}
            {formFields && formFields.length > 0 ? (
              formFields.map((field, index) => {
                console.log(`Attempting to render field ${index}:`, field);
                return renderFormField(field, index);
              })
            ) : (
              <>
                <div className="md:col-span-1">
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="md:col-span-1">
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleChange}
                    placeholder="Your Email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div className="md:col-span-1">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    placeholder="Phone Number (Optional)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-1">
                  <input
                    type="text"
                    name="company"
                    value={formData.company || ''}
                    onChange={handleChange}
                    placeholder="Company Name (Optional)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <textarea
                    name="message"
                    value={formData.message || ''}
                    onChange={handleChange}
                    placeholder="Your Message"
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  ></textarea>
                </div>
              </>
            )}
    
            {/* CAPTCHA */}
            {enableCaptcha && (
              <div className="mt-4">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcRxxxxxxxxxxxxxxxxxxx'}
                  onChange={handleCaptchaChange}
                />
              </div>
            )}
            
            {/* Submit Status Message */}
            {submitStatus.message && (
              <div className={`p-4 rounded-lg ${
                submitStatus.success 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {submitStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`${buttonFullWidth ? 'w-full' : ''} px-6 py-3 ${fieldBorderRadius} text-lg font-medium transition-colors duration-200`}
              style={{ 
                backgroundColor: isSubmitting ? '#9CA3AF' : buttonColor,
                color: buttonTextColor
              }}
            >
              {isSubmitting ? 'Sending...' : submitButtonText}
            </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactFormSection;














// // BEFORE HAVING NEW INOUTES TYPOES
// // src/components/sections/ContactFormSection.js
// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import ReCAPTCHA from 'react-google-recaptcha';

// const ContactFormSection = ({ content }) => {
//   const [formData, setFormData] = useState({});
//   const [submitStatus, setSubmitStatus] = useState({
//     success: false,
//     error: false,
//     message: ''
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [captchaValue, setCaptchaValue] = useState(null);
  
//   // Initialize form fields based on configuration or defaults
//   useEffect(() => {
//     const initialFormData = {};
    
//     // Get form fields from content or use defaults
//     const formFields = content?.formFields || [
//       { id: 'name', type: 'text', required: true },
//       { id: 'email', type: 'email', required: true },
//       { id: 'phone', type: 'tel', required: false },
//       { id: 'company', type: 'text', required: false },
//       { id: 'message', type: 'textarea', required: true }
//     ];
    
//     // Initialize form data with empty values
//     formFields.forEach(field => {
//       initialFormData[field.id] = '';
//     });
    
//     setFormData(initialFormData);
//   }, [content?.formFields]);
  
//   // Extract content props or use defaults
//   const title = content?.title || "Send Us a Message";
//   const submitButtonText = content?.submitButtonText || "Send Message";
//   const successMessage = content?.successMessage || "Thank you for your message! We'll get back to you soon.";
//   const errorMessage = content?.errorMessage || "Sorry, there was an error sending your message. Please try again.";
//   const contactInfo = content?.contactInfo || {
//     phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
//     email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
//     address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
//     hours: {
//       weekday: "Monday - Friday: 9 AM - 5 PM",
//       weekend: "Saturday - Sunday: Closed"
//     },
//     socialMedia: {
//       facebook: "",
//       twitter: "",
//       instagram: "",
//       linkedin: ""
//     }
//   };
  
//   // Layout and style properties
//   const showContactInfo = content?.showContactInfo !== false;
//   const layoutType = content?.layoutType || 'side-by-side';
//   const backgroundColor = content?.backgroundColor || "#1A4B84";
//   const textColor = content?.textColor || "#ffffff";
//   const formBackgroundColor = content?.formBackgroundColor || "#ffffff";
//   const buttonColor = content?.buttonColor || backgroundColor;
//   const buttonTextColor = content?.buttonTextColor || "#ffffff";
//   const fieldBorderColor = content?.fieldBorderColor || "#e5e7eb";
//   const borderRadius = content?.borderRadius || "rounded-xl";
//   const shadowStyle = content?.shadowStyle || "shadow-xl";
//   const padding = content?.padding || "p-12";
//   const fieldBorderRadius = content?.fieldBorderRadius || "rounded-lg";
//   const showSocialIcons = content?.showSocialIcons !== false;
//   const buttonFullWidth = content?.buttonFullWidth !== false;
//   const enableCaptcha = content?.enableCaptcha === true;
  
//   // Handle form input changes
//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value
//     }));
//   };
  
//   // CAPTCHA change handler
//   const handleCaptchaChange = (value) => {
//     setCaptchaValue(value);
//   };
  
//   // Form validation
//   const validateForm = () => {
//     // Get form fields or use defaults
//     const formFields = content?.formFields || [
//       { id: 'name', type: 'text', required: true },
//       { id: 'email', type: 'email', required: true },
//       { id: 'message', type: 'textarea', required: true }
//     ];
    
//     // Check each required field
//     for (const field of formFields) {
//       if (field.required && !formData[field.id]) {
//         setSubmitStatus({ 
//           success: false, 
//           error: true, 
//           message: `Please fill in the ${field.label || field.id} field.` 
//         });
//         return false;
//       }
//     }
    
//     // Check email format if there's an email field
//     const emailField = formFields.find(field => field.type === 'email');
//     if (emailField && formData[emailField.id]) {
//       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       if (!emailRegex.test(formData[emailField.id])) {
//         setSubmitStatus({ 
//           success: false, 
//           error: true, 
//           message: 'Please enter a valid email address.' 
//         });
//         return false;
//       }
//     }
    
//     // Check CAPTCHA if enabled
//     if (enableCaptcha && !captchaValue) {
//       setSubmitStatus({ 
//         success: false, 
//         error: true, 
//         message: 'Please complete the CAPTCHA verification.' 
//       });
//       return false;
//     }

//     return true;
//   };
  
//   // Form submission handler
//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     // Reset previous status
//     setSubmitStatus({ success: false, error: false, message: '' });
    
//     // Validate form
//     if (!validateForm()) return;

//     // Prevent multiple submissions
//     if (isSubmitting) return;
//     setIsSubmitting(true);

//     try {
//       // Create data object with form values and CAPTCHA token if enabled
//       const submitData = {
//         ...formData,
//         ...(enableCaptcha && { captchaToken: captchaValue })
//       };
      
//       // Send form data to backend
//       const response = await axios.post('/api/contact', submitData);
      
//       setSubmitStatus({
//         success: true,
//         error: false,
//         message: successMessage
//       });
      
//       // Reset form
//       const resetFormData = {};
//       Object.keys(formData).forEach(key => {
//         resetFormData[key] = '';
//       });
//       setFormData(resetFormData);
      
//       // Reset CAPTCHA
//       if (enableCaptcha) {
//         setCaptchaValue(null);
//         // Attempt to reset CAPTCHA widget if the ref is available
//         if (window.grecaptcha) {
//           try {
//             window.grecaptcha.reset();
//           } catch (err) {
//             console.error('Error resetting CAPTCHA:', err);
//           }
//         }
//       }
//     } catch (error) {
//       setSubmitStatus({
//         success: false,
//         error: true,
//         message: error.response?.data?.message || errorMessage
//       });
//       console.error('Form submission error:', error);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };
  
//   // Render a form field based on its type
//   const renderFormField = (field, index) => {
//     console.log(`Rendering field ${index}:`, field);
//     console.log('Field type:', field.type);
//     console.log('Field width:', field.width);

    
//     // Determine the width class based on field configuration
//     let widthClass = 'col-span-2'; // Default to full width
//     if (field.width === 'half') {
//       widthClass = 'sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1';
//     } else if (field.width === 'third') {
//       widthClass = 'md:col-span-1 lg:col-span-1';
//     }
    
        
//     const fieldClasses = `${field.width} px-3 py-2 border ${fieldBorderRadius} focus:outline-none focus:ring-2 focus:ring-blue-500 w-full`;
//     const fieldStyle = { borderColor: fieldBorderColor };
//     console.log('Computed width class:', widthClass);
                      
//     switch (field.type) {
//       case 'text':
//       case 'email':
//       case 'tel':
//       case 'number':
//         console.log(`Rendering ${field.type} input for ${field.id}`);
//         return (
//           <div key={field.id} className={widthClass}>
//             {field.label && (
//               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
//                 {field.label} {field.required && <span className="text-red-500">*</span>}
//               </label>
//             )}
//             <input
//               type={field.type}
//               id={field.id}
//               name={field.id}
//               value={formData[field.id] || ''}
//               onChange={handleChange}
//               placeholder={field.placeholder || ''}
//               className={fieldClasses}
//               style={fieldStyle}
//               required={field.required}
//             />
//           </div>
//         );
        
//       case 'textarea':
//         console.log(`Rendering textarea for ${field.id}`);
//         return (
//           <div key={field.id} className={widthClass}>
//             {field.label && (
//               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
//                 {field.label} {field.required && <span className="text-red-500">*</span>}
//               </label>
//             )}
//             <textarea
//               id={field.id}
//               name={field.id}
//               value={formData[field.id] || ''}
//               onChange={handleChange}
//               placeholder={field.placeholder || ''}
//               rows={field.rows || 5}
//               className={fieldClasses}
//               style={fieldStyle}
//               required={field.required}
//             />
//           </div>
//         );
        
//       case 'select':
//         console.log(`Rendering select for ${field.id}`);
//         // Parse options from comma-separated string or use array
//         const options = typeof field.options === 'string' 
//           ? field.options.split(',').map(opt => opt.trim())
//           : field.options || [];
          
//         return (
//           <div key={field.id} className={widthClass}>
//             {field.label && (
//               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
//                 {field.label} {field.required && <span className="text-red-500">*</span>}
//               </label>
//             )}
//             <select
//               id={field.id}
//               name={field.id}
//               value={formData[field.id] || ''}
//               onChange={handleChange}
//               className={fieldClasses}
//               style={fieldStyle}
//               required={field.required}
//             >
//               <option value="">{field.placeholder || 'Select an option'}</option>
//               {options.map((option, idx) => (
//                 <option key={idx} value={option}>
//                   {option}
//                 </option>
//               ))}
//             </select>
//           </div>
//         );
        
//       case 'checkbox':
//         console.log(`Rendering checkbox for ${field.id}`);
//         return (
//           <div key={field.id} className={widthClass}>
//             <label className="flex items-center">
//               <input
//                 type="checkbox"
//                 id={field.id}
//                 name={field.id}
//                 checked={!!formData[field.id]}
//                 onChange={handleChange}
//                 className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
//                 style={{ borderColor: fieldBorderColor }}
//                 required={field.required}
//               />
//               <span className="ml-2 text-sm text-gray-700">
//                 {field.label} {field.required && <span className="text-red-500">*</span>}
//               </span>
//             </label>
//           </div>
//         );
        
//       default:
//         console.log(`Unknown field type: ${field.type}`);
//         return null;
//     }
//   };

  
//   // Social Media Icons
//   const renderSocialIcon = (platform, url) => {
//     if (!url) return null;
    
//     let icon;
//     switch (platform) {
//       case 'facebook':
//         icon = (
//           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//             <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
//           </svg>
//         );
//         break;
//       case 'twitter':
//         icon = (
//           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//             <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.035 10.035 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z" />
//           </svg>
//         );
//         break;
//       case 'instagram':
//         icon = (
//           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//             <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
//           </svg>
//         );
//         break;
//       case 'linkedin':
//         icon = (
//           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
//             <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
//           </svg>
//         );
//         break;
//       default:
//         return null;
//     }
    
//     return (
//       <a 
//         href={url} 
//         target="_blank" 
//         rel="noopener noreferrer" 
//         className="text-white hover:text-gray-200 transition-colors"
//         key={platform}
//       >
//         {icon}
//       </a>
//     );
//   };
  
//   // Apply custom CSS if provided
//   useEffect(() => {
//     if (content?.customCss) {
//       // Create a style element
//       const styleElement = document.createElement('style');
//       styleElement.textContent = content.customCss;
//       document.head.appendChild(styleElement);
      
//       // Clean up when component unmounts
//       return () => {
//         document.head.removeChild(styleElement);
//       };
//     }
//   }, [content?.customCss]);
  
//   // Determine form layout classes based on configuration
//   const getLayoutClasses = () => {
//     switch (layoutType) {
//       case 'form-top':
//         return {
//           container: `grid grid-cols-1 gap-8`,
//           formSection: 'order-1',
//           infoSection: 'order-2'
//         };
//       case 'info-top':
//         return {
//           container: `grid grid-cols-1 gap-8`,
//           formSection: 'order-2',
//           infoSection: 'order-1'
//         };
//       case 'form-only':
//         return {
//           container: ``,
//           formSection: 'w-full',
//           infoSection: 'hidden'
//         };
//       case 'side-by-side':
//       default:
//         return {
//           container: `grid md:grid-cols-2 gap-8`,
//           formSection: '',
//           infoSection: ''
//         };
//     }
//   };
  
//   const layoutClasses = getLayoutClasses();
  
//   // Get form fields from content or use defaults
//   const formFields = content?.formFields || [
//     { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
//     { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
//     { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
//     { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
//     { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
//   ];

//   return (
//     <div className={`${borderRadius} overflow-hidden ${shadowStyle} bg-white contact-form-section`}>
//       <div className={layoutClasses.container}>
//         {/* Contact Information Section */}
//         {showContactInfo && (
//           <div 
//             className={`${padding} flex flex-col justify-center ${layoutClasses.infoSection}`} 
//             style={{ backgroundColor, color: textColor }}
//           >
//             <h2 className="text-3xl md:text-4xl font-bold mb-6">Contact {process.env.REACT_APP_COMPANY_NAME || 'Us'}</h2>
//             <div className="space-y-6">
//               <div className="flex items-center space-x-4">
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
//                 </svg>
//                 <span className="text-lg">{contactInfo.phone}</span>
//               </div>
//               <div className="flex items-center space-x-4">
//                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
//                 </svg>
//                 <span className="text-lg">{contactInfo.email}</span>
//               </div>
//               {contactInfo.address && (
//                 <div className="flex items-center space-x-4">
//                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
//                   </svg>
//                   <span className="text-lg">{contactInfo.address}</span>
//                 </div>
//               )}
//               <div className="mt-6">
//                 <h3 className="text-xl font-semibold mb-3">Business Hours</h3>
//                 <p className="text-lg">{contactInfo.hours.weekday}</p>
//                 <p className="text-lg">{contactInfo.hours.weekend}</p>
//               </div>
              
//               {/* Social Media Icons */}
              
//               {showSocialIcons && (
//                 <div className="mt-6">
//                   <h3 className="text-xl font-semibold mb-3">Connect With Us</h3>
//                   <div className="flex space-x-4">
//                     {Object.entries(contactInfo.socialMedia || {}).map(([platform, url]) => 
//                       url && renderSocialIcon(platform, url)
//                     )}
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {/* Contact Form Section */}
//         <div 
//           className={`${padding} ${layoutClasses.formSection}`}
//           style={{ backgroundColor: formBackgroundColor }}
//         >
//           <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{title}</h2>
//           <form onSubmit={handleSubmit} className="space-y-6">
//            <div className="grid grid-cols-2 gap-4">

//             {/* <div className="grid grid-cols-2 gap-4">
//               {formFields.map((field, index) => renderFormField(field, index))}
//             </div>
//              */}
//             {/* Fallback to direct form fields if formFields mapping fails */}
//             {formFields && formFields.length > 0 ? (
//               formFields.map((field, index) => {
//                 console.log(`Attempting to render field ${index}:`, field);
//                 return renderFormField(field, index);
//               })
//             ) : (
//               <>
//                 <div className="md:col-span-1">
//                   <input
//                     type="text"
//                     name="name"
//                     value={formData.name || ''}
//                     onChange={handleChange}
//                     placeholder="Your Name"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   />
//                 </div>
//                 <div className="md:col-span-1">
//                   <input
//                     type="email"
//                     name="email"
//                     value={formData.email || ''}
//                     onChange={handleChange}
//                     placeholder="Your Email"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   />
//                 </div>
//                 <div className="md:col-span-1">
//                   <input
//                     type="tel"
//                     name="phone"
//                     value={formData.phone || ''}
//                     onChange={handleChange}
//                     placeholder="Phone Number (Optional)"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//                 <div className="md:col-span-1">
//                   <input
//                     type="text"
//                     name="company"
//                     value={formData.company || ''}
//                     onChange={handleChange}
//                     placeholder="Company Name (Optional)"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   />
//                 </div>
//                 <div className="col-span-2">
//                   <textarea
//                     name="message"
//                     value={formData.message || ''}
//                     onChange={handleChange}
//                     placeholder="Your Message"
//                     rows="5"
//                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   ></textarea>
//                 </div>
//               </>
//             )}
    
//             {/* CAPTCHA */}
//             {enableCaptcha && (
//               <div className="mt-4">
//                 <ReCAPTCHA
//                   sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcRxxxxxxxxxxxxxxxxxxx'}
//                   onChange={handleCaptchaChange}
//                 />
//               </div>
//             )}
            
//             {/* Submit Status Message */}
//             {submitStatus.message && (
//               <div className={`p-4 rounded-lg ${
//                 submitStatus.success 
//                   ? 'bg-green-100 text-green-800' 
//                   : 'bg-red-100 text-red-800'
//               }`}>
//                 {submitStatus.message}
//               </div>
//             )}

//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className={`${buttonFullWidth ? 'w-full' : ''} px-6 py-3 ${fieldBorderRadius} text-lg font-medium transition-colors duration-200`}
//               style={{ 
//                 backgroundColor: isSubmitting ? '#9CA3AF' : buttonColor,
//                 color: buttonTextColor
//               }}
//             >
//               {isSubmitting ? 'Sending...' : submitButtonText}
//             </button>
//             </div>

//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ContactFormSection;


















// // // src/components/sections/ContactFormSection.js
// // import React, { useState, useEffect } from 'react';
// // import axios from 'axios';
// // import ReCAPTCHA from 'react-google-recaptcha';

// // const ContactFormSection = ({ content }) => {
// //   const [formData, setFormData] = useState({});
// //   const [submitStatus, setSubmitStatus] = useState({
// //     success: false,
// //     error: false,
// //     message: ''
// //   });
// //   const [isSubmitting, setIsSubmitting] = useState(false);
// //   const [captchaValue, setCaptchaValue] = useState(null);
  
// //   // Initialize form fields based on configuration or defaults
// //   useEffect(() => {
// //     const initialFormData = {};
    
// //     // Get form fields from content or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'phone', type: 'tel', required: false },
// //       { id: 'company', type: 'text', required: false },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Initialize form data with empty values
// //     formFields.forEach(field => {
// //       initialFormData[field.id] = '';
// //     });
    
// //     setFormData(initialFormData);
// //   }, [content?.formFields]);
  
// //   // Extract content props or use defaults
// //   const title = content?.title || "Send Us a Message";
// //   const submitButtonText = content?.submitButtonText || "Send Message";
// //   const successMessage = content?.successMessage || "Thank you for your message! We'll get back to you soon.";
// //   const errorMessage = content?.errorMessage || "Sorry, there was an error sending your message. Please try again.";
// //   const contactInfo = content?.contactInfo || {
// //     phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
// //     email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
// //     address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
// //     hours: {
// //       weekday: "Monday - Friday: 9 AM - 5 PM",
// //       weekend: "Saturday - Sunday: Closed"
// //     },
// //     socialMedia: {
// //       facebook: "",
// //       twitter: "",
// //       instagram: "",
// //       linkedin: ""
// //     }
// //   };
  
// //   // Layout and style properties
// //   const showContactInfo = content?.showContactInfo !== false;
// //   const layoutType = content?.layoutType || 'side-by-side';
// //   const backgroundColor = content?.backgroundColor || "#1A4B84";
// //   const textColor = content?.textColor || "#ffffff";
// //   const formBackgroundColor = content?.formBackgroundColor || "#ffffff";
// //   const buttonColor = content?.buttonColor || backgroundColor;
// //   const buttonTextColor = content?.buttonTextColor || "#ffffff";
// //   const fieldBorderColor = content?.fieldBorderColor || "#e5e7eb";
// //   const borderRadius = content?.borderRadius || "rounded-xl";
// //   const shadowStyle = content?.shadowStyle || "shadow-xl";
// //   const padding = content?.padding || "p-12";
// //   const fieldBorderRadius = content?.fieldBorderRadius || "rounded-lg";
// //   const showSocialIcons = content?.showSocialIcons !== false;
// //   const buttonFullWidth = content?.buttonFullWidth !== false;
// //   const enableCaptcha = content?.enableCaptcha === true;
  
// //   // Handle form input changes
// //   const handleChange = (e) => {
// //     const { name, value, type, checked } = e.target;
// //     setFormData(prev => ({
// //       ...prev,
// //       [name]: type === 'checkbox' ? checked : value
// //     }));
// //   };
  
// //   // CAPTCHA change handler
// //   const handleCaptchaChange = (value) => {
// //     setCaptchaValue(value);
// //   };
  
// //   // Form validation
// //   const validateForm = () => {
// //     // Get form fields or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Check each required field
// //     for (const field of formFields) {
// //       if (field.required && !formData[field.id]) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: `Please fill in the ${field.label || field.id} field.` 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check email format if there's an email field
// //     const emailField = formFields.find(field => field.type === 'email');
// //     if (emailField && formData[emailField.id]) {
// //       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// //       if (!emailRegex.test(formData[emailField.id])) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: 'Please enter a valid email address.' 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check CAPTCHA if enabled
// //     if (enableCaptcha && !captchaValue) {
// //       setSubmitStatus({ 
// //         success: false, 
// //         error: true, 
// //         message: 'Please complete the CAPTCHA verification.' 
// //       });
// //       return false;
// //     }

// //     return true;
// //   };
  
// //   // Form submission handler
// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
    
// //     // Reset previous status
// //     setSubmitStatus({ success: false, error: false, message: '' });
    
// //     // Validate form
// //     if (!validateForm()) return;

// //     // Prevent multiple submissions
// //     if (isSubmitting) return;
// //     setIsSubmitting(true);

// //     try {
// //       // Create data object with form values and CAPTCHA token if enabled
// //       const submitData = {
// //         ...formData,
// //         ...(enableCaptcha && { captchaToken: captchaValue })
// //       };
      
// //       // Send form data to backend
// //       const response = await axios.post('/api/contact', submitData);
      
// //       setSubmitStatus({
// //         success: true,
// //         error: false,
// //         message: successMessage
// //       });
      
// //       // Reset form
// //       const resetFormData = {};
// //       Object.keys(formData).forEach(key => {
// //         resetFormData[key] = '';
// //       });
// //       setFormData(resetFormData);
      
// //       // Reset CAPTCHA
// //       if (enableCaptcha) {
// //         setCaptchaValue(null);
// //         // Attempt to reset CAPTCHA widget if the ref is available
// //         if (window.grecaptcha) {
// //           try {
// //             window.grecaptcha.reset();
// //           } catch (err) {
// //             console.error('Error resetting CAPTCHA:', err);
// //           }
// //         }
// //       }
// //     } catch (error) {
// //       setSubmitStatus({
// //         success: false,
// //         error: true,
// //         message: error.response?.data?.message || errorMessage
// //       });
// //       console.error('Form submission error:', error);
// //     } finally {
// //       setIsSubmitting(false);
// //     }
// //   };
  
// //   // Render a form field based on its type
// //   const renderFormField = (field, index) => {
// //     console.log(`Rendering field ${index}:`, field);
// //     console.log('Field type:', field.type);
// //     console.log('Field width:', field.width);
    
// //     const fieldClasses = `w-full px-4 py-3 border ${fieldBorderRadius} focus:outline-none focus:ring-2 focus:ring-blue-500`;
// //     const fieldStyle = { borderColor: fieldBorderColor };
    
// //     // Determine the width class based on field configuration
// //     let widthClass = 'col-span-2'; // Default to full width
// //     if (field.width === 'half') {
// //       widthClass = 'md:col-span-1';
// //     } else if (field.width === 'third') {
// //       widthClass = 'md:col-span-1 lg:col-span-1';
// //     }
    
// //     console.log('Computed width class:', widthClass);
                      
// //     switch (field.type) {
// //       case 'text':
// //       case 'email':
// //       case 'tel':
// //       case 'number':
// //         console.log(`Rendering ${field.type} input for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <input
// //               type={field.type}
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'textarea':
// //         console.log(`Rendering textarea for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <textarea
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               rows={field.rows || 5}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'select':
// //         console.log(`Rendering select for ${field.id}`);
// //         // Parse options from comma-separated string or use array
// //         const options = typeof field.options === 'string' 
// //           ? field.options.split(',').map(opt => opt.trim())
// //           : field.options || [];
          
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <select
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             >
// //               <option value="">{field.placeholder || 'Select an option'}</option>
// //               {options.map((option, idx) => (
// //                 <option key={idx} value={option}>
// //                   {option}
// //                 </option>
// //               ))}
// //             </select>
// //           </div>
// //         );
        
// //       case 'checkbox':
// //         console.log(`Rendering checkbox for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             <label className="flex items-center">
// //               <input
// //                 type="checkbox"
// //                 id={field.id}
// //                 name={field.id}
// //                 checked={!!formData[field.id]}
// //                 onChange={handleChange}
// //                 className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
// //                 style={{ borderColor: fieldBorderColor }}
// //                 required={field.required}
// //               />
// //               <span className="ml-2 text-sm text-gray-700">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </span>
// //             </label>
// //           </div>
// //         );
        
// //       default:
// //         console.log(`Unknown field type: ${field.type}`);
// //         return null;
// //     }
// //   };

  
// //   // Social Media Icons
// //   const renderSocialIcon = (platform, url) => {
// //     if (!url) return null;
    
// //     let icon;
// //     switch (platform) {
// //       case 'facebook':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
// //           </svg>
// //         );
// //         break;
// //       case 'twitter':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.035 10.035 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z" />
// //           </svg>
// //         );
// //         break;
// //       case 'instagram':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
// //           </svg>
// //         );
// //         break;
// //       case 'linkedin':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
// //           </svg>
// //         );
// //         break;
// //       default:
// //         return null;
// //     }
    
// //     return (
// //       <a 
// //         href={url} 
// //         target="_blank" 
// //         rel="noopener noreferrer" 
// //         className="text-white hover:text-gray-200 transition-colors"
// //         key={platform}
// //       >
// //         {icon}
// //       </a>
// //     );
// //   };
  
// //   // Apply custom CSS if provided
// //   useEffect(() => {
// //     if (content?.customCss) {
// //       // Create a style element
// //       const styleElement = document.createElement('style');
// //       styleElement.textContent = content.customCss;
// //       document.head.appendChild(styleElement);
      
// //       // Clean up when component unmounts
// //       return () => {
// //         document.head.removeChild(styleElement);
// //       };
// //     }
// //   }, [content?.customCss]);
  
// //   // Determine form layout classes based on configuration
// //   const getLayoutClasses = () => {
// //     switch (layoutType) {
// //       case 'form-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-1',
// //           infoSection: 'order-2'
// //         };
// //       case 'info-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-2',
// //           infoSection: 'order-1'
// //         };
// //       case 'form-only':
// //         return {
// //           container: ``,
// //           formSection: 'w-full',
// //           infoSection: 'hidden'
// //         };
// //       case 'side-by-side':
// //       default:
// //         return {
// //           container: `grid md:grid-cols-2 gap-8`,
// //           formSection: '',
// //           infoSection: ''
// //         };
// //     }
// //   };
  
// //   const layoutClasses = getLayoutClasses();
  
// //   // Get form fields from content or use defaults
// //   const formFields = content?.formFields || [
// //     { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
// //     { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
// //     { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
// //     { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
// //     { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
// //   ];

// //   return (
// //     <div className={`${borderRadius} overflow-hidden ${shadowStyle} bg-white contact-form-section`}>
// //       <div className={layoutClasses.container}>
// //         {/* Contact Information Section */}
// //         {showContactInfo && (
// //           <div 
// //             className={`${padding} flex flex-col justify-center ${layoutClasses.infoSection}`} 
// //             style={{ backgroundColor, color: textColor }}
// //           >
// //             <h2 className="text-3xl md:text-4xl font-bold mb-6">Contact {process.env.REACT_APP_COMPANY_NAME || 'Us'}</h2>
// //             <div className="space-y-6">
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.phone}</span>
// //               </div>
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.email}</span>
// //               </div>
// //               {contactInfo.address && (
// //                 <div className="flex items-center space-x-4">
// //                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
// //                   </svg>
// //                   <span className="text-lg">{contactInfo.address}</span>
// //                 </div>
// //               )}
// //               <div className="mt-6">
// //                 <h3 className="text-xl font-semibold mb-3">Business Hours</h3>
// //                 <p className="text-lg">{contactInfo.hours.weekday}</p>
// //                 <p className="text-lg">{contactInfo.hours.weekend}</p>
// //               </div>
              
// //               {/* Social Media Icons */}
// //               {showSocialIcons && (
// //                 <div className="mt-6">
// //                   <h3 className="text-xl font-semibold mb-3">Connect With Us</h3>
// //                   <div className="flex space-x-4">
// //                     {Object.entries(contactInfo.socialMedia || {}).map(([platform, url]) => 
// //                       url && renderSocialIcon(platform, url)
// //                     )}
// //                   </div>
// //                 </div>
// //               )}
// //             </div>
// //           </div>
// //         )}

// //         {/* Contact Form Section */}
// //         <div 
// //           className={`${padding} ${layoutClasses.formSection}`}
// //           style={{ backgroundColor: formBackgroundColor }}
// //         >
// //           <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{title}</h2>
// //           <form onSubmit={handleSubmit} className="space-y-6">
// //             {/* <div className="grid grid-cols-2 gap-4">
// //               {formFields.map((field, index) => renderFormField(field, index))}
// //             </div>
// //              */}
// //             {/* Fallback to direct form fields if formFields mapping fails */}
// //             {formFields && formFields.length > 0 ? (
// //               formFields.map((field, index) => {
// //                 console.log(`Attempting to render field ${index}:`, field);
// //                 return renderFormField(field, index);
// //               })
// //             ) : (
// //               <>
// //                 <div className="md:col-span-1">
// //                   <input
// //                     type="text"
// //                     name="name"
// //                     value={formData.name || ''}
// //                     onChange={handleChange}
// //                     placeholder="Your Name"
// //                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //                     required
// //                   />
// //                 </div>
// //                 <div className="md:col-span-1">
// //                   <input
// //                     type="email"
// //                     name="email"
// //                     value={formData.email || ''}
// //                     onChange={handleChange}
// //                     placeholder="Your Email"
// //                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //                     required
// //                   />
// //                 </div>
// //                 <div className="md:col-span-1">
// //                   <input
// //                     type="tel"
// //                     name="phone"
// //                     value={formData.phone || ''}
// //                     onChange={handleChange}
// //                     placeholder="Phone Number (Optional)"
// //                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //                   />
// //                 </div>
// //                 <div className="md:col-span-1">
// //                   <input
// //                     type="text"
// //                     name="company"
// //                     value={formData.company || ''}
// //                     onChange={handleChange}
// //                     placeholder="Company Name (Optional)"
// //                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //                   />
// //                 </div>
// //                 <div className="col-span-2">
// //                   <textarea
// //                     name="message"
// //                     value={formData.message || ''}
// //                     onChange={handleChange}
// //                     placeholder="Your Message"
// //                     rows="5"
// //                     className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //                     required
// //                   ></textarea>
// //                 </div>
// //               </>
// //             )}
    
// //             {/* CAPTCHA */}
// //             {enableCaptcha && (
// //               <div className="mt-4">
// //                 <ReCAPTCHA
// //                   sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcRxxxxxxxxxxxxxxxxxxx'}
// //                   onChange={handleCaptchaChange}
// //                 />
// //               </div>
// //             )}
            
// //             {/* Submit Status Message */}
// //             {submitStatus.message && (
// //               <div className={`p-4 rounded-lg ${
// //                 submitStatus.success 
// //                   ? 'bg-green-100 text-green-800' 
// //                   : 'bg-red-100 text-red-800'
// //               }`}>
// //                 {submitStatus.message}
// //               </div>
// //             )}

// //             <button
// //               type="submit"
// //               disabled={isSubmitting}
// //               className={`${buttonFullWidth ? 'w-full' : ''} px-6 py-3 ${fieldBorderRadius} text-lg font-medium transition-colors duration-200`}
// //               style={{ 
// //                 backgroundColor: isSubmitting ? '#9CA3AF' : buttonColor,
// //                 color: buttonTextColor
// //               }}
// //             >
// //               {isSubmitting ? 'Sending...' : submitButtonText}
// //             </button>
// //           </form>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default ContactFormSection;

















// // // src/components/sections/ContactFormSection.js
// // import React, { useState, useEffect } from 'react';
// // import axios from 'axios';
// // import ReCAPTCHA from 'react-google-recaptcha';

// // const ContactFormSection = ({ content }) => {
// //   const [formData, setFormData] = useState({});
// //   const [submitStatus, setSubmitStatus] = useState({
// //     success: false,
// //     error: false,
// //     message: ''
// //   });
// //   const [isSubmitting, setIsSubmitting] = useState(false);
// //   const [captchaValue, setCaptchaValue] = useState(null);
  
// //   // Initialize form fields based on configuration or defaults
// //   useEffect(() => {
// //     const initialFormData = {};
    
// //     // Get form fields from content or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'phone', type: 'tel', required: false },
// //       { id: 'company', type: 'text', required: false },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Initialize form data with empty values
// //     formFields.forEach(field => {
// //       initialFormData[field.id] = '';
// //     });
    
// //     setFormData(initialFormData);
// //   }, [content?.formFields]);
  
// //   // Extract content props or use defaults
// //   const title = content?.title || "Send Us a Message";
// //   const submitButtonText = content?.submitButtonText || "Send Message";
// //   const successMessage = content?.successMessage || "Thank you for your message! We'll get back to you soon.";
// //   const errorMessage = content?.errorMessage || "Sorry, there was an error sending your message. Please try again.";
// //   const contactInfo = content?.contactInfo || {
// //     phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
// //     email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
// //     address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
// //     hours: {
// //       weekday: "Monday - Friday: 9 AM - 5 PM",
// //       weekend: "Saturday - Sunday: Closed"
// //     },
// //     socialMedia: {
// //       facebook: "",
// //       twitter: "",
// //       instagram: "",
// //       linkedin: ""
// //     }
// //   };
  
// //   // Layout and style properties
// //   const showContactInfo = content?.showContactInfo !== false;
// //   const layoutType = content?.layoutType || 'side-by-side';
// //   const backgroundColor = content?.backgroundColor || "#1A4B84";
// //   const textColor = content?.textColor || "#ffffff";
// //   const formBackgroundColor = content?.formBackgroundColor || "#ffffff";
// //   const buttonColor = content?.buttonColor || backgroundColor;
// //   const buttonTextColor = content?.buttonTextColor || "#ffffff";
// //   const fieldBorderColor = content?.fieldBorderColor || "#e5e7eb";
// //   const borderRadius = content?.borderRadius || "rounded-xl";
// //   const shadowStyle = content?.shadowStyle || "shadow-xl";
// //   const padding = content?.padding || "p-12";
// //   const fieldBorderRadius = content?.fieldBorderRadius || "rounded-lg";
// //   const showSocialIcons = content?.showSocialIcons !== false;
// //   const buttonFullWidth = content?.buttonFullWidth !== false;
// //   const enableCaptcha = content?.enableCaptcha === true;
  
// //   // Handle form input changes
// //   const handleChange = (e) => {
// //     const { name, value, type, checked } = e.target;
// //     setFormData(prev => ({
// //       ...prev,
// //       [name]: type === 'checkbox' ? checked : value
// //     }));
// //   };
  
// //   // CAPTCHA change handler
// //   const handleCaptchaChange = (value) => {
// //     setCaptchaValue(value);
// //   };
  
// //   // Form validation
// //   const validateForm = () => {
// //     // Get form fields or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Check each required field
// //     for (const field of formFields) {
// //       if (field.required && !formData[field.id]) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: `Please fill in the ${field.label || field.id} field.` 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check email format if there's an email field
// //     const emailField = formFields.find(field => field.type === 'email');
// //     if (emailField && formData[emailField.id]) {
// //       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// //       if (!emailRegex.test(formData[emailField.id])) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: 'Please enter a valid email address.' 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check CAPTCHA if enabled
// //     if (enableCaptcha && !captchaValue) {
// //       setSubmitStatus({ 
// //         success: false, 
// //         error: true, 
// //         message: 'Please complete the CAPTCHA verification.' 
// //       });
// //       return false;
// //     }

// //     return true;
// //   };
  
// //   // Form submission handler
// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
    
// //     // Reset previous status
// //     setSubmitStatus({ success: false, error: false, message: '' });
    
// //     // Validate form
// //     if (!validateForm()) return;

// //     // Prevent multiple submissions
// //     if (isSubmitting) return;
// //     setIsSubmitting(true);

// //     try {
// //       // Create data object with form values and CAPTCHA token if enabled
// //       const submitData = {
// //         ...formData,
// //         ...(enableCaptcha && { captchaToken: captchaValue })
// //       };
      
// //       // Send form data to backend
// //       const response = await axios.post('/api/contact', submitData);
      
// //       setSubmitStatus({
// //         success: true,
// //         error: false,
// //         message: successMessage
// //       });
      
// //       // Reset form
// //       const resetFormData = {};
// //       Object.keys(formData).forEach(key => {
// //         resetFormData[key] = '';
// //       });
// //       setFormData(resetFormData);
      
// //       // Reset CAPTCHA
// //       if (enableCaptcha) {
// //         setCaptchaValue(null);
// //         // Attempt to reset CAPTCHA widget if the ref is available
// //         if (window.grecaptcha) {
// //           try {
// //             window.grecaptcha.reset();
// //           } catch (err) {
// //             console.error('Error resetting CAPTCHA:', err);
// //           }
// //         }
// //       }
// //     } catch (error) {
// //       setSubmitStatus({
// //         success: false,
// //         error: true,
// //         message: error.response?.data?.message || errorMessage
// //       });
// //       console.error('Form submission error:', error);
// //     } finally {
// //       setIsSubmitting(false);
// //     }
// //   };
  
// //   // Render a form field based on its type
// //   const renderFormField = (field, index) => {
// //     console.log(`Rendering field ${index}:`, field);
// //     console.log('Field type:', field.type);
// //     console.log('Field width:', field.width);
    
// //     const fieldClasses = `w-full px-4 py-3 border ${fieldBorderRadius} focus:outline-none focus:ring-2 focus:ring-blue-500`;
// //     const fieldStyle = { borderColor: fieldBorderColor };
    
// //     // Determine the width class based on field configuration
// //     let widthClass = 'col-span-2'; // Default to full width
// //     if (field.width === 'half') {
// //       widthClass = 'md:col-span-1';
// //     } else if (field.width === 'third') {
// //       widthClass = 'md:col-span-1 lg:col-span-1';
// //     }
    
// //     console.log('Computed width class:', widthClass);
                      
// //     switch (field.type) {
// //       case 'text':
// //       case 'email':
// //       case 'tel':
// //       case 'number':
// //         console.log(`Rendering ${field.type} input for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <input
// //               type={field.type}
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'textarea':
// //         console.log(`Rendering textarea for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <textarea
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               rows={field.rows || 5}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'select':
// //         console.log(`Rendering select for ${field.id}`);
// //         // Parse options from comma-separated string or use array
// //         const options = typeof field.options === 'string' 
// //           ? field.options.split(',').map(opt => opt.trim())
// //           : field.options || [];
          
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <select
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             >
// //               <option value="">{field.placeholder || 'Select an option'}</option>
// //               {options.map((option, idx) => (
// //                 <option key={idx} value={option}>
// //                   {option}
// //                 </option>
// //               ))}
// //             </select>
// //           </div>
// //         );
        
// //       case 'checkbox':
// //         console.log(`Rendering checkbox for ${field.id}`);
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             <label className="flex items-center">
// //               <input
// //                 type="checkbox"
// //                 id={field.id}
// //                 name={field.id}
// //                 checked={!!formData[field.id]}
// //                 onChange={handleChange}
// //                 className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
// //                 style={{ borderColor: fieldBorderColor }}
// //                 required={field.required}
// //               />
// //               <span className="ml-2 text-sm text-gray-700">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </span>
// //             </label>
// //           </div>
// //         );
        
// //       default:
// //         console.log(`Unknown field type: ${field.type}`);
// //         return null;
// //     }
// //   };

  
// //   // Social Media Icons
// //   const renderSocialIcon = (platform, url) => {
// //     if (!url) return null;
    
// //     let icon;
// //     switch (platform) {
// //       case 'facebook':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
// //           </svg>
// //         );
// //         break;
// //       case 'twitter':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.035 10.035 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z" />
// //           </svg>
// //         );
// //         break;
// //       case 'instagram':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
// //           </svg>
// //         );
// //         break;
// //       case 'linkedin':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
// //           </svg>
// //         );
// //         break;
// //       default:
// //         return null;
// //     }
    
// //     return (
// //       <a 
// //         href={url} 
// //         target="_blank" 
// //         rel="noopener noreferrer" 
// //         className="text-white hover:text-gray-200 transition-colors"
// //         key={platform}
// //       >
// //         {icon}
// //       </a>
// //     );
// //   };
  
// //   // Apply custom CSS if provided
// //   useEffect(() => {
// //     if (content?.customCss) {
// //       // Create a style element
// //       const styleElement = document.createElement('style');
// //       styleElement.textContent = content.customCss;
// //       document.head.appendChild(styleElement);
      
// //       // Clean up when component unmounts
// //       return () => {
// //         document.head.removeChild(styleElement);
// //       };
// //     }
// //   }, [content?.customCss]);
  
// //   // Determine form layout classes based on configuration
// //   const getLayoutClasses = () => {
// //     switch (layoutType) {
// //       case 'form-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-1',
// //           infoSection: 'order-2'
// //         };
// //       case 'info-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-2',
// //           infoSection: 'order-1'
// //         };
// //       case 'form-only':
// //         return {
// //           container: ``,
// //           formSection: 'w-full',
// //           infoSection: 'hidden'
// //         };
// //       case 'side-by-side':
// //       default:
// //         return {
// //           container: `grid md:grid-cols-2 gap-8`,
// //           formSection: '',
// //           infoSection: ''
// //         };
// //     }
// //   };
  
// //   const layoutClasses = getLayoutClasses();
  
// //   // Get form fields from content or use defaults
// //   const formFields = content?.formFields || [
// //     { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
// //     { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
// //     { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
// //     { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
// //     { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
// //   ];

// //   return (
// //     <div className={`${borderRadius} overflow-hidden ${shadowStyle} bg-white contact-form-section`}>
// //       <div className={layoutClasses.container}>
// //         {/* Contact Information Section */}
// //         {showContactInfo && (
// //           <div 
// //             className={`${padding} flex flex-col justify-center ${layoutClasses.infoSection}`} 
// //             style={{ backgroundColor, color: textColor }}
// //           >
// //             <h2 className="text-3xl md:text-4xl font-bold mb-6">Contact {process.env.REACT_APP_COMPANY_NAME || 'Us'}</h2>
// //             <div className="space-y-6">
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.phone}</span>
// //               </div>
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.email}</span>
// //               </div>
// //               {contactInfo.address && (
// //                 <div className="flex items-center space-x-4">
// //                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
// //                   </svg>
// //                   <span className="text-lg">{contactInfo.address}</span>
// //                 </div>
// //               )}
// //               <div className="mt-6">
// //                 <h3 className="text-xl font-semibold mb-3">Business Hours</h3>
// //                 <p className="text-lg">{contactInfo.hours.weekday}</p>
// //                 <p className="text-lg">{contactInfo.hours.weekend}</p>
// //               </div>
              
// //               {/* Social Media Icons */}
// //               {showSocialIcons && (
// //                 <div className="mt-6">
// //                   <h3 className="text-xl font-semibold mb-3">Connect With Us</h3>
// //                   <div className="flex space-x-4">
// //                     {Object.entries(contactInfo.socialMedia || {}).map(([platform, url]) => 
// //                       url && renderSocialIcon(platform, url)
// //                     )}
// //                   </div>
// //                 </div>
// //               )}
// //             </div>
// //           </div>
// //         )}

// //         {/* Contact Form Section */}
// //         <div 
// //           className={`${padding} ${layoutClasses.formSection}`}
// //           style={{ backgroundColor: formBackgroundColor }}
// //         >
// //           <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{title}</h2>
// //           <form onSubmit={handleSubmit} className="space-y-6">
// //             {/* <div className="grid grid-cols-2 gap-4">
// //               {formFields.map((field, index) => renderFormField(field, index))}
// //             </div>
// //              */}
// //               {/* Debug output */}
// //     {console.log('About to render form fields, count:', formFields?.length || 0)}
    
// //     {/* Fallback to direct form fields if formFields mapping fails */}
// //     {formFields && formFields.length > 0 ? (
// //       formFields.map((field, index) => {
// //         console.log(`Attempting to render field ${index}:`, field);
// //         return renderFormField(field, index);
// //       })
// //     ) : (
// //       <>
// //         <div className="md:col-span-1">
// //           <input
// //             type="text"
// //             name="name"
// //             value={formData.name || ''}
// //             onChange={handleChange}
// //             placeholder="Your Name"
// //             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //             required
// //           />
// //         </div>
// //         <div className="md:col-span-1">
// //           <input
// //             type="email"
// //             name="email"
// //             value={formData.email || ''}
// //             onChange={handleChange}
// //             placeholder="Your Email"
// //             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //             required
// //           />
// //         </div>
// //         <div className="md:col-span-1">
// //           <input
// //             type="tel"
// //             name="phone"
// //             value={formData.phone || ''}
// //             onChange={handleChange}
// //             placeholder="Phone Number (Optional)"
// //             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //           />
// //         </div>
// //         <div className="md:col-span-1">
// //           <input
// //             type="text"
// //             name="company"
// //             value={formData.company || ''}
// //             onChange={handleChange}
// //             placeholder="Company Name (Optional)"
// //             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //           />
// //         </div>
// //         <div className="col-span-2">
// //           <textarea
// //             name="message"
// //             value={formData.message || ''}
// //             onChange={handleChange}
// //             placeholder="Your Message"
// //             rows="5"
// //             className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
// //             required
// //           ></textarea>
// //         </div>
// //       </>
// //     )}
    
// //             {/* CAPTCHA */}
// //             {enableCaptcha && (
// //               <div className="mt-4">
// //                 <ReCAPTCHA
// //                   sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcRxxxxxxxxxxxxxxxxxxx'}
// //                   onChange={handleCaptchaChange}
// //                 />
// //               </div>
// //             )}
            
// //             {/* Submit Status Message */}
// //             {submitStatus.message && (
// //               <div className={`p-4 rounded-lg ${
// //                 submitStatus.success 
// //                   ? 'bg-green-100 text-green-800' 
// //                   : 'bg-red-100 text-red-800'
// //               }`}>
// //                 {submitStatus.message}
// //               </div>
// //             )}

// //             <button
// //               type="submit"
// //               disabled={isSubmitting}
// //               className={`${buttonFullWidth ? 'w-full' : ''} px-6 py-3 ${fieldBorderRadius} text-lg font-medium transition-colors duration-200`}
// //               style={{ 
// //                 backgroundColor: isSubmitting ? '#9CA3AF' : buttonColor,
// //                 color: buttonTextColor
// //               }}
// //             >
// //               {isSubmitting ? 'Sending...' : submitButtonText}
// //             </button>
// //           </form>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default ContactFormSection;
















// // // src/components/sections/ContactFormSection.js
// // import React, { useState, useEffect } from 'react';
// // import axios from 'axios';
// // import ReCAPTCHA from 'react-google-recaptcha';

// // const ContactFormSection = ({ content }) => {
// //   const [formData, setFormData] = useState({});
// //   const [submitStatus, setSubmitStatus] = useState({
// //     success: false,
// //     error: false,
// //     message: ''
// //   });
// //   const [isSubmitting, setIsSubmitting] = useState(false);
// //   const [captchaValue, setCaptchaValue] = useState(null);
  
// //   // Initialize form fields based on configuration or defaults
// //   useEffect(() => {
// //     const initialFormData = {};
    
// //     // Get form fields from content or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'phone', type: 'tel', required: false },
// //       { id: 'company', type: 'text', required: false },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Initialize form data with empty values
// //     formFields.forEach(field => {
// //       initialFormData[field.id] = '';
// //     });
    
// //     setFormData(initialFormData);
// //   }, [content?.formFields]);
  
// //   // Extract content props or use defaults
// //   const title = content?.title || "Send Us a Message";
// //   const submitButtonText = content?.submitButtonText || "Send Message";
// //   const successMessage = content?.successMessage || "Thank you for your message! We'll get back to you soon.";
// //   const errorMessage = content?.errorMessage || "Sorry, there was an error sending your message. Please try again.";
// //   const contactInfo = content?.contactInfo || {
// //     phone: process.env.REACT_APP_COMPANY_PHONE_NUMBER || "+1 (833) 333-3101",
// //     email: process.env.REACT_APP_COMPANY_EMAIL || "Info@bagbox.ca",
// //     address: process.env.REACT_APP_COMPANY_ADDRESS || "102-1127 14th St W, North Vancouver, BC, V7P 1J9",
// //     hours: {
// //       weekday: "Monday - Friday: 9 AM - 5 PM",
// //       weekend: "Saturday - Sunday: Closed"
// //     },
// //     socialMedia: {
// //       facebook: "",
// //       twitter: "",
// //       instagram: "",
// //       linkedin: ""
// //     }
// //   };
  
// //   // Layout and style properties
// //   const showContactInfo = content?.showContactInfo !== false;
// //   const layoutType = content?.layoutType || 'side-by-side';
// //   const backgroundColor = content?.backgroundColor || "#1A4B84";
// //   const textColor = content?.textColor || "#ffffff";
// //   const formBackgroundColor = content?.formBackgroundColor || "#ffffff";
// //   const buttonColor = content?.buttonColor || backgroundColor;
// //   const buttonTextColor = content?.buttonTextColor || "#ffffff";
// //   const fieldBorderColor = content?.fieldBorderColor || "#e5e7eb";
// //   const borderRadius = content?.borderRadius || "rounded-xl";
// //   const shadowStyle = content?.shadowStyle || "shadow-xl";
// //   const padding = content?.padding || "p-12";
// //   const fieldBorderRadius = content?.fieldBorderRadius || "rounded-lg";
// //   const showSocialIcons = content?.showSocialIcons !== false;
// //   const buttonFullWidth = content?.buttonFullWidth !== false;
// //   const enableCaptcha = content?.enableCaptcha === true;
  
// //   // Handle form input changes
// //   const handleChange = (e) => {
// //     const { name, value, type, checked } = e.target;
// //     setFormData(prev => ({
// //       ...prev,
// //       [name]: type === 'checkbox' ? checked : value
// //     }));
// //   };
  
// //   // CAPTCHA change handler
// //   const handleCaptchaChange = (value) => {
// //     setCaptchaValue(value);
// //   };
  
// //   // Form validation
// //   const validateForm = () => {
// //     // Get form fields or use defaults
// //     const formFields = content?.formFields || [
// //       { id: 'name', type: 'text', required: true },
// //       { id: 'email', type: 'email', required: true },
// //       { id: 'message', type: 'textarea', required: true }
// //     ];
    
// //     // Check each required field
// //     for (const field of formFields) {
// //       if (field.required && !formData[field.id]) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: `Please fill in the ${field.label || field.id} field.` 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check email format if there's an email field
// //     const emailField = formFields.find(field => field.type === 'email');
// //     if (emailField && formData[emailField.id]) {
// //       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// //       if (!emailRegex.test(formData[emailField.id])) {
// //         setSubmitStatus({ 
// //           success: false, 
// //           error: true, 
// //           message: 'Please enter a valid email address.' 
// //         });
// //         return false;
// //       }
// //     }
    
// //     // Check CAPTCHA if enabled
// //     if (enableCaptcha && !captchaValue) {
// //       setSubmitStatus({ 
// //         success: false, 
// //         error: true, 
// //         message: 'Please complete the CAPTCHA verification.' 
// //       });
// //       return false;
// //     }

// //     return true;
// //   };
  
// //   // Form submission handler
// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
    
// //     // Reset previous status
// //     setSubmitStatus({ success: false, error: false, message: '' });
    
// //     // Validate form
// //     if (!validateForm()) return;

// //     // Prevent multiple submissions
// //     if (isSubmitting) return;
// //     setIsSubmitting(true);

// //     try {
// //       // Create data object with form values and CAPTCHA token if enabled
// //       const submitData = {
// //         ...formData,
// //         ...(enableCaptcha && { captchaToken: captchaValue })
// //       };
      
// //       // Send form data to backend
// //       const response = await axios.post('/api/contact', submitData);
      
// //       setSubmitStatus({
// //         success: true,
// //         error: false,
// //         message: successMessage
// //       });
      
// //       // Reset form
// //       const resetFormData = {};
// //       Object.keys(formData).forEach(key => {
// //         resetFormData[key] = '';
// //       });
// //       setFormData(resetFormData);
      
// //       // Reset CAPTCHA
// //       if (enableCaptcha) {
// //         setCaptchaValue(null);
// //         // Attempt to reset CAPTCHA widget if the ref is available
// //         if (window.grecaptcha) {
// //           try {
// //             window.grecaptcha.reset();
// //           } catch (err) {
// //             console.error('Error resetting CAPTCHA:', err);
// //           }
// //         }
// //       }
// //     } catch (error) {
// //       setSubmitStatus({
// //         success: false,
// //         error: true,
// //         message: error.response?.data?.message || errorMessage
// //       });
// //       console.error('Form submission error:', error);
// //     } finally {
// //       setIsSubmitting(false);
// //     }
// //   };
  
// //   // Render a form field based on its type
// //   const renderFormField = (field, index) => {
// //     const fieldClasses = `w-full px-4 py-3 border ${fieldBorderRadius} focus:outline-none focus:ring-2 focus:ring-blue-500`;
// //     const fieldStyle = { borderColor: fieldBorderColor };
    
// //     // Determine the width class based on field configuration
// //     const widthClass = field.width === 'half' ? 'md:col-span-1' : 
// //                       field.width === 'third' ? 'md:col-span-1 lg:col-span-1' : 'col-span-2';
                      
// //     switch (field.type) {
// //       case 'text':
// //       case 'email':
// //       case 'tel':
// //       case 'number':
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <input
// //               type={field.type}
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'textarea':
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <textarea
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               placeholder={field.placeholder || ''}
// //               rows={field.rows || 5}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             />
// //           </div>
// //         );
        
// //       case 'select':
// //         // Parse options from comma-separated string or use array
// //         const options = typeof field.options === 'string' 
// //           ? field.options.split(',').map(opt => opt.trim())
// //           : field.options || [];
          
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             {field.label && (
// //               <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </label>
// //             )}
// //             <select
// //               id={field.id}
// //               name={field.id}
// //               value={formData[field.id] || ''}
// //               onChange={handleChange}
// //               className={fieldClasses}
// //               style={fieldStyle}
// //               required={field.required}
// //             >
// //               <option value="">{field.placeholder || 'Select an option'}</option>
// //               {options.map((option, idx) => (
// //                 <option key={idx} value={option}>
// //                   {option}
// //                 </option>
// //               ))}
// //             </select>
// //           </div>
// //         );
        
// //       case 'checkbox':
// //         return (
// //           <div key={field.id} className={widthClass}>
// //             <label className="flex items-center">
// //               <input
// //                 type="checkbox"
// //                 id={field.id}
// //                 name={field.id}
// //                 checked={!!formData[field.id]}
// //                 onChange={handleChange}
// //                 className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
// //                 style={{ borderColor: fieldBorderColor }}
// //                 required={field.required}
// //               />
// //               <span className="ml-2 text-sm text-gray-700">
// //                 {field.label} {field.required && <span className="text-red-500">*</span>}
// //               </span>
// //             </label>
// //           </div>
// //         );
        
// //       default:
// //         return null;
// //     }
// //   };
  
// //   // Social Media Icons
// //   const renderSocialIcon = (platform, url) => {
// //     if (!url) return null;
    
// //     let icon;
// //     switch (platform) {
// //       case 'facebook':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
// //           </svg>
// //         );
// //         break;
// //       case 'twitter':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.035 10.035 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.937 4.937 0 004.604 3.417 9.868 9.868 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.054 0 13.999-7.496 13.999-13.986 0-.209 0-.42-.015-.63a9.936 9.936 0 002.46-2.548l-.047-.02z" />
// //           </svg>
// //         );
// //         break;
// //       case 'instagram':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
// //           </svg>
// //         );
// //         break;
// //       case 'linkedin':
// //         icon = (
// //           <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
// //             <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
// //           </svg>
// //         );
// //         break;
// //       default:
// //         return null;
// //     }
    
// //     return (
// //       <a 
// //         href={url} 
// //         target="_blank" 
// //         rel="noopener noreferrer" 
// //         className="text-white hover:text-gray-200 transition-colors"
// //         key={platform}
// //       >
// //         {icon}
// //       </a>
// //     );
// //   };
  
// //   // Apply custom CSS if provided
// //   useEffect(() => {
// //     if (content?.customCss) {
// //       // Create a style element
// //       const styleElement = document.createElement('style');
// //       styleElement.textContent = content.customCss;
// //       document.head.appendChild(styleElement);
      
// //       // Clean up when component unmounts
// //       return () => {
// //         document.head.removeChild(styleElement);
// //       };
// //     }
// //   }, [content?.customCss]);
  
// //   // Determine form layout classes based on configuration
// //   const getLayoutClasses = () => {
// //     switch (layoutType) {
// //       case 'form-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-1',
// //           infoSection: 'order-2'
// //         };
// //       case 'info-top':
// //         return {
// //           container: `grid grid-cols-1 gap-8`,
// //           formSection: 'order-2',
// //           infoSection: 'order-1'
// //         };
// //       case 'form-only':
// //         return {
// //           container: ``,
// //           formSection: 'w-full',
// //           infoSection: 'hidden'
// //         };
// //       case 'side-by-side':
// //       default:
// //         return {
// //           container: `grid md:grid-cols-2 gap-8`,
// //           formSection: '',
// //           infoSection: ''
// //         };
// //     }
// //   };
  
// //   const layoutClasses = getLayoutClasses();
  
// //   // Get form fields from content or use defaults
// //   const formFields = content?.formFields || [
// //     { id: 'name', type: 'text', label: 'Your Name', placeholder: 'Your Name', required: true, width: 'half' },
// //     { id: 'email', type: 'email', label: 'Your Email', placeholder: 'Your Email', required: true, width: 'half' },
// //     { id: 'phone', type: 'tel', label: 'Phone Number', placeholder: 'Phone Number (Optional)', required: false, width: 'half' },
// //     { id: 'company', type: 'text', label: 'Company Name', placeholder: 'Company Name (Optional)', required: false, width: 'half' },
// //     { id: 'message', type: 'textarea', label: 'Your Message', placeholder: 'Your Message', required: true, width: 'full' }
// //   ];

// //   return (
// //     <div className={`${borderRadius} overflow-hidden ${shadowStyle} bg-white contact-form-section`}>
// //       <div className={layoutClasses.container}>
// //         {/* Contact Information Section */}
// //         {showContactInfo && (
// //           <div 
// //             className={`${padding} flex flex-col justify-center ${layoutClasses.infoSection}`} 
// //             style={{ backgroundColor, color: textColor }}
// //           >
// //             <h2 className="text-3xl md:text-4xl font-bold mb-6">Contact {process.env.REACT_APP_COMPANY_NAME || 'Us'}</h2>
// //             <div className="space-y-6">
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.phone}</span>
// //               </div>
// //               <div className="flex items-center space-x-4">
// //                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
// //                 </svg>
// //                 <span className="text-lg">{contactInfo.email}</span>
// //               </div>
// //               {contactInfo.address && (
// //                 <div className="flex items-center space-x-4">
// //                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
// //                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
// //                   </svg>
// //                   <span className="text-lg">{contactInfo.address}</span>
// //                 </div>
// //               )}
// //               <div className="mt-6">
// //                 <h3 className="text-xl font-semibold mb-3">Business Hours</h3>
// //                 <p className="text-lg">{contactInfo.hours.weekday}</p>
// //                 <p className="text-lg">{contactInfo.hours.weekend}</p>
// //               </div>
              
// //               {/* Social Media Icons */}
// //               {showSocialIcons && (
// //                 <div className="mt-6">
// //                   <h3 className="text-xl font-semibold mb-3">Connect With Us</h3>
// //                   <div className="flex space-x-4">
// //                     {Object.entries(contactInfo.socialMedia || {}).map(([platform, url]) => 
// //                       url && renderSocialIcon(platform, url)
// //                     )}
// //                   </div>
// //                 </div>
// //               )}
// //             </div>
// //           </div>
// //         )}

// //         {/* Contact Form Section */}
// //         <div 
// //           className={`${padding} ${layoutClasses.formSection}`}
// //           style={{ backgroundColor: formBackgroundColor }}
// //         >
// //           <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{title}</h2>
// //           <form onSubmit={handleSubmit} className="space-y-6">
// //             <div className="grid grid-cols-2 gap-4">
// //               {formFields.map((field, index) => renderFormField(field, index))}
// //             </div>
            
// //             {/* CAPTCHA */}
// //             {enableCaptcha && (
// //               <div className="mt-4">
// //                 <ReCAPTCHA
// //                   sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcRxxxxxxxxxxxxxxxxxxx'}
// //                   onChange={handleCaptchaChange}
// //                 />
// //               </div>
// //             )}
            
// //             {/* Submit Status Message */}
// //             {submitStatus.message && (
// //               <div className={`p-4 rounded-lg ${
// //                 submitStatus.success 
// //                   ? 'bg-green-100 text-green-800' 
// //                   : 'bg-red-100 text-red-800'
// //               }`}>
// //                 {submitStatus.message}
// //               </div>
// //             )}

// //             <button
// //               type="submit"
// //               disabled={isSubmitting}
// //               className={`${buttonFullWidth ? 'w-full' : ''} px-6 py-3 ${fieldBorderRadius} text-lg font-medium transition-colors duration-200`}
// //               style={{ 
// //                 backgroundColor: isSubmitting ? '#9CA3AF' : buttonColor,
// //                 color: buttonTextColor
// //               }}
// //             >
// //               {isSubmitting ? 'Sending...' : submitButtonText}
// //             </button>
// //           </form>
// //         </div>
// //       </div>
// //     </div>
// //   );
// // };

// // export default ContactFormSection;



